import React, { useState } from "react";
import { createTermGroup, updateTermGroup } from "@/api/termGroups";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ArrowLeft } from "lucide-react";

export default function TermGroupForm({ termGroup, onClose }) {
    const [formData, setFormData] = useState({
        name: termGroup?.name || "",
        description: termGroup?.description || "",
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [errors, setErrors] = useState({});

    const isEditing = !!termGroup;

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        
        // Clear error when user starts typing
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: null
            }));
        }
    };

    const validateForm = () => {
        const newErrors = {};
        
        if (!formData.name.trim()) {
            newErrors.name = "Name is required";
        }
        
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!validateForm()) {
            return;
        }

        setIsSubmitting(true);
        
        try {
            const payload = {
                name: formData.name.trim(),
                description: formData.description.trim() || null,
            };

            if (isEditing) {
                await updateTermGroup(termGroup.id, payload);
                toast.success("Term group updated successfully");
            } else {
                await createTermGroup(payload);
                toast.success("Term group created successfully");
            }
            
            onClose();
        } catch (error) {
            const errorMessage = error?.message || `Failed to ${isEditing ? 'update' : 'create'} term group`;
            toast.error(errorMessage);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="p-6 max-w-2xl mx-auto">
            <div className="mb-6">
                <Button 
                    variant="ghost" 
                    onClick={onClose}
                    className="mb-4 flex items-center gap-2"
                >
                    <ArrowLeft className="h-4 w-4" />
                    Back to Term Groups
                </Button>
                
                <h1 className="text-2xl font-bold text-[#5D1F42]">
                    {isEditing ? 'Edit Term Group' : 'Create Term Group'}
                </h1>
            </div>

            <Card>
                <CardHeader>
                    <CardTitle>
                        {isEditing ? `Editing "${termGroup.name}"` : 'New Term Group'}
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    <form onSubmit={handleSubmit} className="space-y-6">
                        <div>
                            <Label htmlFor="name">Name *</Label>
                            <Input
                                id="name"
                                name="name"
                                value={formData.name}
                                onChange={handleChange}
                                placeholder="Enter term group name"
                                className={errors.name ? "border-red-500" : ""}
                            />
                            {errors.name && (
                                <p className="text-red-500 text-sm mt-1">{errors.name}</p>
                            )}
                        </div>

                        <div>
                            <Label htmlFor="description">Description</Label>
                            <Textarea
                                id="description"
                                name="description"
                                value={formData.description}
                                onChange={handleChange}
                                placeholder="Optional description for this term group"
                                rows={4}
                            />
                        </div>

                        <div className="flex gap-3">
                            <Button 
                                type="submit" 
                                disabled={isSubmitting}
                                className="min-w-24"
                            >
                                {isSubmitting ? 'Saving...' : (isEditing ? 'Update' : 'Create')}
                            </Button>
                            <Button 
                                type="button" 
                                variant="outline" 
                                onClick={onClose}
                                disabled={isSubmitting}
                            >
                                Cancel
                            </Button>
                        </div>
                    </form>
                </CardContent>
            </Card>
        </div>
    );
}